import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const stages = [
  { key: 'applied', name: 'Applied', count: 24 },
  { key: 'screening', name: 'AI Screening', count: 12 },
  { key: 'interview', name: 'Interview', count: 5 },
  { key: 'offer', name: 'Offer', count: 2 },
];

const candidates = [
  { id: '1', name: 'Sarah Chen', score: 94, stage: 'interview', avatar: 'SC' },
  { id: '2', name: 'Michael Park', score: 88, stage: 'interview', avatar: 'MP' },
  { id: '3', name: 'Emily Rodriguez', score: 92, stage: 'screening', avatar: 'ER' },
  { id: '4', name: 'James Wilson', score: 85, stage: 'screening', avatar: 'JW' },
  { id: '5', name: 'Lisa Thompson', score: 91, stage: 'offer', avatar: 'LT' },
  { id: '6', name: 'David Kim', score: 78, stage: 'applied', avatar: 'DK' },
  { id: '7', name: 'Anna Martinez', score: 82, stage: 'applied', avatar: 'AM' },
];

function getScoreColor(score: number) {
  if (score >= 90) return 'text-green-600 bg-green-50 border-green-200';
  if (score >= 80) return 'text-blue-600 bg-blue-50 border-blue-200';
  if (score >= 70) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-muted-foreground bg-muted border-border';
}

export function LandingPipelineDemo() {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      {/* Stage Headers */}
      <div className="grid grid-cols-4 gap-3">
        {stages.map((stage) => (
          <div
            key={stage.key}
            className="text-center p-2 rounded-lg bg-muted/50"
          >
            <div className="text-sm font-medium text-heading">{stage.name}</div>
            <div className="text-xs text-muted-foreground">{stage.count} candidates</div>
          </div>
        ))}
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-4 gap-3 min-h-[200px]">
        {stages.map((stage) => {
          const stageCandidates = candidates.filter((c) => c.stage === stage.key);
          return (
            <div
              key={stage.key}
              className="space-y-2 p-2 rounded-lg bg-muted/30 border border-border/50"
            >
              {stageCandidates.slice(0, 2).map((candidate) => (
                <div
                  key={candidate.id}
                  className={cn(
                    'p-3 rounded-lg bg-card border border-border transition-all duration-200 cursor-pointer',
                    hoveredCard === candidate.id && 'shadow-md border-primary/30 -translate-y-0.5'
                  )}
                  onMouseEnter={() => setHoveredCard(candidate.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                      {candidate.avatar}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{candidate.name}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge
                      variant="outline"
                      className={cn('text-xs', getScoreColor(candidate.score))}
                    >
                      AI: {candidate.score}%
                    </Badge>
                  </div>
                </div>
              ))}
              {stageCandidates.length > 2 && (
                <div className="text-xs text-center text-muted-foreground py-1">
                  +{stageCandidates.length - 2} more
                </div>
              )}
              {stageCandidates.length === 0 && (
                <div className="text-xs text-center text-muted-foreground py-4">
                  Drop here
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Automation Badge */}
      <div className="flex items-center justify-center gap-2 py-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
          </span>
          Auto-ranking candidates based on AI scores
        </div>
      </div>
    </div>
  );
}
